/**
 * advisor-engine.js — eligibility, scoring, ranking and the suggestion pick
 * (spec docs/superpowers/specs/2026-10-12-plan-advisor-design.md, "Eligibility", "Ranking").
 *
 * Pure: takes the quoted plan list exactly as script.js holds it (allPlansWithPremiums), the
 * fact-find, the rule set and the CMS coverage results, and returns everything the results page
 * shows — the suggestion, alternatives, add-ons, the plans-not-shown list and the warnings — plus a
 * compact summary for the saved session. Nothing here reads the DOM or the network.
 */

import { normalizePlan } from './advisor-normalize.js';
import { readRuleSet, resolvePlanRule, TIER_LABELS } from './advisor-rules.js';
import { sanitizeFacts, missingFacts, hasHighHealthNeed } from './advisor-facts.js';
import { scoreFit, scoreAddOn, MEDICAL_TYPES } from './advisor-fit.js';

export const MAX_ALTERNATIVES = 3;

const ADD_ON_TYPES = {
  dental: ['dental'],
  vision: ['vision'],
  accident: ['accident'],
  criticalIllness: ['critical-illness', 'specified-disease'],
};

const UNDERWRITTEN = ['simplified', 'underwritten'];

/**
 * Basic eligibility (stage 1). Detailed knockout questions arrive with the carrier guides (stage 4).
 * @returns {{ status: 'eligible'|'check_with_carrier'|'not_eligible', reason: string|null }}
 */
export function checkEligibility(plan, facts, state) {
  const ages = facts.members.map((m) => m.age).filter((a) => a != null);
  // A minimum age is an applicant floor; children ride the adult's policy.
  const adultAges = facts.members.filter((m) => m.role !== 'child').map((m) => m.age).filter((a) => a != null);
  if (plan.minAge != null && adultAges.some((a) => a < plan.minAge)) {
    return { status: 'not_eligible', reason: `Covers ages ${plan.minAge} and up` };
  }
  if (plan.maxAge != null && ages.some((a) => a > plan.maxAge)) {
    return { status: 'not_eligible', reason: `Covers ages up to ${plan.maxAge}` };
  }
  if (Array.isArray(plan.states) && state && !plan.states.includes(state)) {
    return { status: 'not_eligible', reason: `Not offered in ${state}` };
  }
  if (UNDERWRITTEN.includes(plan.issueType) && hasHighHealthNeed(facts)) {
    return { status: 'check_with_carrier', reason: 'Health questions may decline this applicant' };
  }
  if (plan.issueType === 'unknown') {
    return { status: 'check_with_carrier', reason: 'Issue type not stated by the carrier' };
  }
  return { status: 'eligible', reason: null };
}

function entryOf(item) {
  return {
    planId: item.plan.planId,
    groupKey: item.groupKey,
    name: item.plan.name,
    carrier: item.plan.carrier,
    productType: item.plan.productType,
    premium: item.plan.premium,
    fit: item.fit.score,
    tier: item.tier,
    tierLabel: TIER_LABELS[item.tier] || null,
    eligibility: item.eligibility.status,
    eligibilityReason: item.eligibility.reason,
    reasons: item.fit.reasons.slice(0, 4),
    warnings: item.warnings,
    raw: item.raw,
  };
}

function byTierThenFit(a, b) {
  return (a.tier - b.tier) || (b.fit.score - a.fit.score) || ((a.plan.premium ?? Infinity) - (b.plan.premium ?? Infinity));
}

function byFit(a, b) {
  return (b.fit.score - a.fit.score) || (a.tier - b.tier) || ((a.plan.premium ?? Infinity) - (b.plan.premium ?? Infinity));
}

// One representative per plan group (the card the agent sees), chosen the same way the suggestion is.
function representatives(items) {
  const byGroup = new Map();
  items.forEach((it) => {
    const cur = byGroup.get(it.groupKey);
    if (!cur || byTierThenFit(it, cur) < 0) byGroup.set(it.groupKey, it);
  });
  return [...byGroup.values()];
}

/**
 * Run the advisor.
 *
 * @param {Object} input
 * @param {Object[]} input.plans - allPlansWithPremiums
 * @param {Object} input.facts - fact-find (advisor-facts.js)
 * @param {Object|null} input.ruleSet - stored rule set, or null when it failed to load
 * @param {string|null} input.state - the quote's state
 * @param {Function} [input.coverageFor] - (rawPlan) → { doctors, drugs } | null (CMS checks)
 * @param {Function} [input.groupKeyOf] - (rawPlan) → card group key (PlanGrouping.groupKeyFor)
 * @param {Set<string>} [input.revealed] - group keys the agent chose to show despite the rules
 * @param {boolean} [input.carrierUnavailable] - a quoting source failed this run
 */
export function runAdvisor(input) {
  const facts = sanitizeFacts(input.facts);
  const rulesUnavailable = !input.ruleSet;
  const ruleSet = readRuleSet(input.ruleSet);
  const state = input.state || facts.state || null;
  const groupKeyOf = typeof input.groupKeyOf === 'function' ? input.groupKeyOf : (p) => String(p && p.id);
  const coverageFor = typeof input.coverageFor === 'function' ? input.coverageFor : () => null;
  const revealed = input.revealed instanceof Set ? input.revealed : new Set();

  const items = (Array.isArray(input.plans) ? input.plans : []).map((raw) => {
    const plan = normalizePlan(raw);
    const groupKey = String(groupKeyOf(raw) ?? plan.planId);
    const rule = resolvePlanRule(plan, ruleSet, state);
    const eligibility = checkEligibility(plan, facts, state);
    return { raw, plan, groupKey, rule, tier: rule.tier, eligibility };
  });

  // Hidden plans leave the results page unless the agent revealed their group.
  const hiddenIds = new Set();
  const hiddenGroups = new Map();
  items.forEach((it) => {
    const byRule = it.rule.hidden;
    const byEligibility = it.eligibility.status === 'not_eligible';
    it.hidden = (byRule || byEligibility) && !revealed.has(it.groupKey);
    if (!byRule && !byEligibility) return;
    if (it.hidden) hiddenIds.add(String(it.plan.planId));
    const g = hiddenGroups.get(it.groupKey) || {
      groupKey: it.groupKey,
      name: it.plan.name,
      carrier: it.plan.carrier,
      productType: it.plan.productType,
      premiumFrom: it.plan.premium,
      planIds: [],
      kind: byRule ? 'rule' : 'eligibility',
      reason: byRule ? (it.rule.rule.note || 'Hidden by a company rule') : it.eligibility.reason,
      revealed: revealed.has(it.groupKey),
    };
    g.planIds.push(it.plan.planId);
    if (it.plan.premium != null && (g.premiumFrom == null || it.plan.premium < g.premiumFrom)) {
      g.premiumFrom = it.plan.premium;
    }
    hiddenGroups.set(it.groupKey, g);
  });

  // Candidates never include hidden or not-eligible plans, even revealed ones: a reveal puts a plan
  // back on the page for the agent; it does not overrule the rule for the suggestion.
  const candidates = items.filter((it) => !it.rule.hidden && it.eligibility.status !== 'not_eligible');
  const medical = candidates.filter((it) => MEDICAL_TYPES.includes(it.plan.productType));
  medical.forEach((it) => {
    it.fit = scoreFit(it.plan, facts, ruleSet.weights, coverageFor(it.raw));
    it.warnings = [...it.fit.warnings];
    if (it.eligibility.status === 'check_with_carrier') it.warnings.push('check_with_carrier');
  });

  const reps = representatives(medical);
  const strong = reps.filter((it) => it.fit.score >= ruleSet.fitFloor).sort(byTierThenFit);
  let outcome;
  let suggestion = null;
  let alternatives = [];
  let closestMatches = [];
  if (reps.length === 0) {
    outcome = 'no_plans';
  } else if (strong.length === 0) {
    outcome = 'no_strong_fit';
    closestMatches = [...reps].sort(byFit).slice(0, MAX_ALTERNATIVES).map(entryOf);
  } else {
    outcome = 'suggested';
    const pick = strong[0];
    suggestion = entryOf(pick);
    alternatives = reps.filter((it) => it !== pick).sort(byFit).slice(0, MAX_ALTERNATIVES).map(entryOf);
  }
  const leanOverrodeFit = !!suggestion && alternatives.some((a) => a.fit > suggestion.fit);

  const addOns = {};
  Object.keys(ADD_ON_TYPES).forEach((key) => {
    if (!facts.addOns[key]) return;
    const pool = representatives(candidates
      .filter((it) => ADD_ON_TYPES[key].includes(it.plan.productType))
      .map((it) => ({ ...it, fit: { score: 0, reasons: [] }, warnings: [] })));
    const cheapest = Math.min(...pool.map((it) => it.plan.premium).filter((p) => p != null));
    pool.forEach((it) => {
      it.fit = scoreAddOn(it.plan, Number.isFinite(cheapest) ? cheapest : null);
      if (it.eligibility.status === 'check_with_carrier') it.warnings.push('check_with_carrier');
    });
    pool.sort(byTierThenFit);
    addOns[key] = pool.length ? entryOf(pool[0]) : null;
  });

  const missing = missingFacts(facts);
  const warnings = new Set();
  if (missing.length) warnings.add('provisional');
  if (rulesUnavailable) warnings.add('rules_unavailable');
  if (input.carrierUnavailable) warnings.add('carrier_unavailable');
  (suggestion ? suggestion.warnings : []).forEach((w) => warnings.add(w));

  return {
    outcome,
    suggestion,
    alternatives,
    closestMatches,
    addOns,
    hidden: [...hiddenGroups.values()],
    hiddenIds,
    warnings: [...warnings],
    missingFacts: missing,
    leanOverrodeFit,
    rulesVersion: ruleSet.version,
    fitFloor: ruleSet.fitFloor,
    // Every medical representative by fit: the page checks doctors and drugs for the top ACA ones.
    ranked: [...reps].sort(byFit).map((it) => ({ planId: String(it.plan.planId), productType: it.plan.productType })),
  };
}

function compactEntry(e) {
  if (!e) return null;
  return {
    planId: String(e.planId).slice(0, 200),
    name: String(e.name || '').slice(0, 120),
    carrier: String(e.carrier || '').slice(0, 80),
    productType: e.productType,
    premium: e.premium,
    fit: e.fit,
    tier: e.tier,
    reasons: (e.reasons || []).slice(0, 3).map((r) => String(r).slice(0, 120)),
    warnings: e.warnings || [],
  };
}

/**
 * The compact record saved with the quote: never whole plan objects, so the request stays far
 * under the live site's 8 KB body limit (spec, "Storage").
 */
export function sessionSummary(result) {
  const addOns = {};
  Object.keys(result.addOns || {}).forEach((k) => { addOns[k] = compactEntry(result.addOns[k]); });
  return {
    outcome: result.outcome,
    suggestion: compactEntry(result.suggestion),
    alternatives: (result.alternatives || []).map(compactEntry),
    closestMatches: (result.closestMatches || []).map(compactEntry),
    addOns,
    hiddenCount: (result.hidden || []).length,
    warnings: result.warnings,
    missingFacts: result.missingFacts,
    leanOverrodeFit: result.leanOverrodeFit,
    rulesVersion: result.rulesVersion,
  };
}

export const AdvisorEngine = { MAX_ALTERNATIVES, checkEligibility, runAdvisor, sessionSummary };
